import { SvgIcon } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DotsThree as DotsThreeIcon } from '@phosphor-icons/react/dist/ssr/DotsThree';
import { Eye as EyeIcon } from '@phosphor-icons/react/dist/ssr/Eye';
import { Lock as LockIcon } from '@phosphor-icons/react/dist/ssr/Lock';
import { paths } from 'config/paths';
import { useRouter } from 'next/router';
import * as React from 'react';

import { usePopover } from '~ui-components/hooks/use-popover';
import { Dataset } from '~ui-components/types/__generated/gql/graphql';

import { DataTypeIcon } from './data-type-icon';
import { ItemIcon } from './item-icon';
import { ItemMenu } from './item-menu';

export interface ItemCardProps {
	item: Dataset;
	onDelete?: (itemId: string) => void;
	size?: 'medium' | 'large';
}

export function ItemCard({
	item,
	onDelete,
	size = 'medium',
}: ItemCardProps): React.JSX.Element {
	const popover = usePopover<HTMLButtonElement>();
	const router = useRouter();

	const handleDelete = React.useCallback(() => {
		popover.handleClose();
		onDelete?.(item.id);
	}, [item, popover, onDelete]);

	const handleCardClick = (datasetId: string) => {
		const href = paths.accountName.data.datasetId.root('john-doe', datasetId);
		router.push(href);
	};

	return (
		<React.Fragment>
			<Card
				key={item.id}
				sx={{
					transition: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
					'&:hover': { boxShadow: 'var(--mui-shadows-16)' },
				}}
			>
				<Stack
					direction="row"
					spacing={3}
					sx={{
						alignItems: 'center',
						justifyContent: 'space-between',
						pt: 2,
						px: 2,
					}}
				>
					<Stack direction="row" spacing={1}>
						<SvgIcon>{item?.isPrivate ? <LockIcon /> : <EyeIcon />}</SvgIcon>
						<SvgIcon>{DataTypeIcon[item.type]}</SvgIcon>
					</Stack>
					<IconButton onClick={popover.handleOpen} ref={popover.anchorRef}>
						<DotsThreeIcon weight="bold" />
					</IconButton>
				</Stack>
				<Stack
					spacing={1}
					padding={2}
					sx={{
						cursor: 'pointer',
					}}
					onClick={() => handleCardClick(item.id)}
				>
					<Box display="flex">
						<ItemIcon size={size} />
					</Box>
					<div>
						<Typography variant="subtitle2">{item.name}</Typography>
						<Stack
							direction="row"
							spacing={1}
							sx={{ alignItems: 'center', justifyContent: 'space-between' }}
						>
							<Typography color="text.secondary" variant="caption">
								{item.size} • Used in {item.mapsCount} maps
							</Typography>
						</Stack>
					</div>
				</Stack>
			</Card>
			<ItemMenu
				anchorEl={popover.anchorRef.current}
				onClose={popover.handleClose}
				onDelete={handleDelete}
				open={popover.open}
			/>
		</React.Fragment>
	);
}
